import { useEffect, useMemo, useRef, useState } from 'react'

export interface SearchableSelectOption {
  value: string
  label: string
}

// A text-input-driven combobox for select fields with too many options to
// scroll through (e.g. a netsapiens domain picker with 3000+ entries) — no
// UI library dependency, matching this codebase's style elsewhere (see
// LineChart's "dependency-free" comment in DeviceDetail.tsx). Filters
// options client-side by substring match on label, case-insensitive.
export default function SearchableSelect({
  options, value, onChange, placeholder, disabled, emptyMessage = 'No matches',
}: {
  options: SearchableSelectOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  emptyMessage?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)

  const selected = options.find(o => o.value === value)

  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  // Cap rendered rows even after filtering — a broad query against 3000+
  // options could still match hundreds; nothing about a dropdown needs to
  // render more than this to be useful, and it keeps typing responsive.
  const maxResults = 200
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const matches = q ? options.filter(o => o.label.toLowerCase().includes(q)) : options
    return matches.slice(0, maxResults)
  }, [options, query])

  const inputSt: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '6px 10px',
    border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14,
  }

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <input
        style={inputSt}
        value={open ? query : (selected?.label ?? '')}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => { setOpen(true); setQuery('') }}
        onBlur={() => setOpen(false)}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
      />
      {open && (
        <div style={{
          position: 'absolute', zIndex: 50, top: '100%', left: 0, right: 0, marginTop: 2,
          background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
          maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        }}>
          {value !== '' && (
            <div
              onClick={() => { onChange(''); setOpen(false) }}
              style={{ padding: '7px 12px', fontSize: 13, color: '#6b7280', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
            >
              — clear selection —
            </div>
          )}
          {filtered.length === 0 && (
            <div style={{ padding: '7px 12px', fontSize: 13, color: '#9ca3af' }}>{emptyMessage}</div>
          )}
          {filtered.map(o => (
            <div
              key={o.value}
              onClick={() => { onChange(o.value); setOpen(false) }}
              style={{
                padding: '7px 12px', fontSize: 13, cursor: 'pointer',
                background: o.value === value ? '#eff6ff' : 'transparent',
              }}
              onMouseDown={e => e.preventDefault()}
            >
              {o.label}
            </div>
          ))}
          {options.length > maxResults && filtered.length === maxResults && (
            <div style={{ padding: '6px 12px', fontSize: 11.5, color: '#9ca3af', borderTop: '1px solid #f3f4f6' }}>
              Showing first {maxResults} matches — keep typing to narrow further.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
